import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { FileText, BookOpen, Loader2 } from 'lucide-react';
import { SearchResult } from '../types/content';
import { useEffect, useRef } from 'react';

interface SearchResultsProps {
  results: SearchResult[];
  loading: boolean;
  query: string;
  selectedIndex: number;
}

export function SearchResults({ results, loading, query, selectedIndex }: SearchResultsProps) {
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedIndex !== -1 && resultsContainerRef.current) {
      const selectedElement = resultsContainerRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
  }, [selectedIndex]);

  if (loading) {
    return (
      <Card className="mt-2 bg-white border-slate-300 shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span>Searching...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (query.length < 2) {
    return null;
  }

  if (results.length === 0) {
    return (
      <Card className="mt-2 bg-white border-slate-300 shadow-lg">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            No results found for "{query}"
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div onMouseDown={(e) => e.preventDefault()}>
      <Card className="mt-2 max-h-96 overflow-y-auto z-50 bg-white border-slate-300 shadow-lg">
        <CardContent className="p-0">
          <div className="space-y-0" ref={resultsContainerRef}>
            {results.map((result, index) => (
              <Link
                key={`${result.type}-${result.slug}`}
                to={result.url}
                className={`group block p-4 hover:bg-primary transition-all duration-200 hover:border-l-4 hover:border-l-secondary ${
                  index !== results.length - 1 ? 'border-b border-slate-200' : ''
                } ${selectedIndex === index ? 'bg-primary/10 border-l-4 border-l-primary' : ''}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    {result.type === 'news' ? (
                      <FileText className="h-4 w-4 text-primary group-hover:text-white transition-colors" />
                    ) : (
                      <BookOpen className="h-4 w-4 text-secondary group-hover:text-white transition-colors" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-sm truncate link-underline group-hover:gradient-text-tech transition-all">
                        {result.title}
                      </h3>
                      <Badge variant="outline" className="text-xs bg-slate-500 text-white border-slate-500 hover:bg-slate-600 transition-colors">
                        {result.type === 'news' ? 'News' : 'Example'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground group-hover:text-white line-clamp-2 transition-colors">
                      {result.excerpt}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}